import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDate, formatNumber } from '../lib/format';
import { Badge, Card, CardHeader } from './ui';

/** A tip as the API sends it: only those about something this person may open ever reach the page. */
interface FeatureTip {
  id: string;
  /** The day it went out, YYYY-MM-DD. */
  releasedOn: string;
  /** The page it is on; none for a tip about the dashboard itself. */
  link: string | null;
  title: string;
  titleAr: string;
  body: string;
  bodyAr: string;
}

const TIPS_KEY = ['feature-tips'] as const;

/** Tips shown before "Show all": the newest, enough to see there is more without filling the dashboard. */
const TIPS_SHOWN = 3;

/**
 * What's new — a tip for each feature shipped lately that this person may open
 * (operator, 2026-11-02: "add tip on dashboard contains new features … must has
 * right on it to know it's exist"). The API decides who is told of what: a tip
 * about something someone may not open never reaches their browser. "Got it" or
 * opening it puts it away for good, on every device — as an opened announcement
 * leaves the dashboard. Nothing to show, no card.
 */
export function FeatureTips() {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const [all, setAll] = useState(false);

  const { data: tips } = useQuery({
    queryKey: TIPS_KEY,
    queryFn: () => api.get<FeatureTip[]>('/feature-tips'),
    // Tips change with a deploy or a click, never by themselves.
    staleTime: 5 * 60_000,
  });

  // Gone from the card at once; the server is told, and the list read again after.
  const hide = (ids: string[] | 'all') =>
    queryClient.setQueryData<FeatureTip[]>(TIPS_KEY, (list) => (ids === 'all' ? [] : list?.filter((tip) => !ids.includes(tip.id))));
  const reread = () => void queryClient.invalidateQueries({ queryKey: TIPS_KEY });
  const dismiss = useMutation({
    mutationFn: (id: string) => api.post(`/feature-tips/${encodeURIComponent(id)}/dismiss`),
    onMutate: (id) => hide([id]),
    onSettled: reread,
  });
  const dismissAll = useMutation({
    mutationFn: () => api.post('/feature-tips/dismiss-all'),
    onMutate: () => hide('all'),
    onSettled: reread,
  });

  if (!tips?.length) return null;
  const shown = all ? tips : tips.slice(0, TIPS_SHOWN);
  const ar = locale === 'ar';

  return (
    <section className="mb-8">
      <Card padded={false}>
        <CardHeader
          className="p-5 pb-0"
          title={
            <span className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 shrink-0 text-signal-700 dark:text-signal" aria-hidden />
              {t('tips.title')}
              <Badge tone="brand" className="tabular">
                {formatNumber(tips.length, locale)}
              </Badge>
            </span>
          }
          subtitle={t('tips.hint')}
          action={
            tips.length > 1 && (
              <button
                type="button"
                onClick={() => dismissAll.mutate()}
                className="text-body-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t('tips.dismissAll')}
              </button>
            )
          }
        />
        <ul>
          {shown.map((tip) => {
            const title = ar ? tip.titleAr : tip.title;
            return (
              <li key={tip.id} className="flex gap-3 border-b hairline px-5 py-3.5 last:border-0">
                <span
                  className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-signal/18 text-signal-800 dark:text-signal"
                  aria-hidden
                >
                  <Sparkles className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-body font-medium text-[var(--text-primary)]">{title}</span>
                    {/* Noon, so the day reads the same in every time zone. */}
                    <span className="text-caption tabular text-muted">{formatDate(`${tip.releasedOn}T12:00:00`, locale)}</span>
                  </p>
                  <p className="mt-0.5 text-body-sm text-muted">{ar ? tip.bodyAr : tip.body}</p>
                  <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                    {tip.link && (
                      <Link
                        to={tip.link}
                        onClick={() => dismiss.mutate(tip.id)}
                        className="text-body-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        {t('tips.open')}
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={() => dismiss.mutate(tip.id)}
                      aria-label={t('tips.gotItLabel', { title })}
                      className="text-body-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:underline"
                    >
                      {t('tips.gotIt')}
                    </button>
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
        {tips.length > TIPS_SHOWN && (
          <div className="border-t hairline px-5 py-2.5">
            <button
              type="button"
              onClick={() => setAll((open) => !open)}
              aria-expanded={all}
              className="text-caption font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              {all ? t('tips.showLess') : t('tips.showAll', { n: formatNumber(tips.length, locale) })}
            </button>
          </div>
        )}
      </Card>
    </section>
  );
}
